// src/components/home/DashboardView.tsx — one saved dashboard, live for the viewer.
//
// Every widget names a report tool; refresh re-runs each one FOR THE CURRENT VIEWER,
// so a property-scoped manager opening a shared dashboard sees their own numbers
// rather than the author's.
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, Sparkles } from 'lucide-react';
import { api } from '../../lib/api';
import { ReportChart, type ChartType } from '../charts/ReportChart';
import { StatTile } from '../charts/StatTile';
import { Button } from '../ui';
import { formatCell } from '../../lib/viz';
import type { Dashboard, ReportResult } from '../../types/api';

export function DashboardView({ dashboard }: { dashboard: Dashboard }) {
  const { data: results, isFetching, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['dashboard-data', dashboard.id],
    queryFn: async () => (await api.post<Record<string, ReportResult>>(`/ai/dashboards/${dashboard.id}/refresh`)).data,
    refetchInterval: 60_000, // widgets carry their own cadence; this is the floor
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {dashboard.prompt
          ? <p className="flex items-center gap-1.5 text-[12.5px] text-ink-500"><Sparkles className="h-3.5 w-3.5 text-bronze-500" /> “{dashboard.prompt}”</p>
          : <p className="text-[12.5px] text-ink-500">{dashboard.description}</p>}
        <div className="flex items-center gap-3">
          {dataUpdatedAt > 0 && <span className="text-[11.5px] text-ink-400">Updated {new Date(dataUpdatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} · live for you</span>}
          <Button variant="secondary" size="sm" onClick={() => void refetch()}><RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} /> Refresh</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {dashboard.widgets?.map((widget) => {
          const result = results?.[widget.id];
          const span = widget.chartType === 'stat' ? 'md:col-span-1' : (widget.layout?.w ?? 2) >= 3 ? 'md:col-span-2 xl:col-span-3' : 'md:col-span-2 xl:col-span-2';

          // A stat tile reads the first numeric total or the first row's y value.
          if (widget.chartType === 'stat') {
            const col = result?.columns?.find((c) => c.key === widget.mapping?.y) ?? result?.columns?.find((c) => ['money', 'number', 'percent'].includes(c.type));
            const raw = result?.totals?.[col?.key ?? ''] ?? (col ? result?.rows?.[0]?.[col.key] : undefined);
            return <div key={widget.id} className={span}><StatTile label={widget.title} value={result ? formatCell(raw, (col?.type ?? 'number') as never, result.currency) : '—'} hint={result?.title} /></div>;
          }

          return (
            <div key={widget.id} className={`surface p-5 ${span}`}>
              <div className="mb-3 flex items-start justify-between gap-2">
                <h3 className="text-[13px] font-semibold text-ink-900">{widget.title}</h3>
                <code className="shrink-0 rounded-full border border-ink-200 bg-ink-50 px-2 py-0.5 font-mono text-[10px] text-ink-500">{widget.toolName}</code>
              </div>
              {result
                ? <ReportChart result={result} chartType={widget.chartType as ChartType} mapping={widget.mapping} height={widget.layout?.h ? widget.layout.h * 80 : 260} />
                : <div className="grid h-[260px] place-items-center text-[13px] text-ink-400">{isFetching ? 'Loading…' : 'No data available for your access level'}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
